import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import {
  X,
  ChevronLeft,
  Search,
  Paperclip,
  Send,
  FileText,
  Download,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { useChat } from "../../contexts/ChatContext";
import { useProject } from "../../contexts/ProjectContext";
import { chatAPI } from "../../utils/chatApi";
import { imageUrl } from "../../utils/endpoints";

// Get 2-letter initials
const getInitials = (name) => {
  if (!name) return "•";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

// Clock format: "4:16 PM"
const fmtClock = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

// Date separator format: "Fri, Oct 9", "Today", "Yesterday"
const fmtDayLabel = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";

  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

// List time format: "Oct 9" or "4:16 PM" if today
const fmtListTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return fmtClock(dateStr);
  }
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

// Role badge styling
// Role badge styling
const getRoleBadgeClass = (role) => {
  const r = (role || "").toLowerCase();
  if (r === "admin") {
    return "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200/80 dark:border-blue-800/80";
  }
  return "bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300 border border-gray-200 dark:border-slate-700";
};

// Soft background for avatar
const getAvatarBg = (name) => {
  const colors = [
    "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
    "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  ];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const TeamChatPanel = () => {
  const {
    isOpen,
    closeChat,
    activeTab,
    setActiveTab,
    activeContact,
    activeConversationId,
    messages,
    conversations,
    contacts,
    projectContacts,
    loadingMessages,
    otherLastReadAt,
    isOtherTyping,
    openChatWithUser,
    openConversation,
    backToList,
    sendMessage,
    notifyTyping,
    fetchUnreadCount,
    fetchConversations,
    setUnreadCount,
    isUserOnline,
  } = useChat();

  const location = useLocation();
  const { currentProject, projects } = useProject();

  const [searchTerm, setSearchTerm] = useState("");
  const [draft, setDraft] = useState("");
  const [pendingUploads, setPendingUploads] = useState([]);
  const [uploading, setUploading] = useState(false);

  const scrollRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimerRef = useRef(null);

  // WhatsApp-style reliable scroll to bottom
  const scrollToBottom = useCallback((behavior = "auto") => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior, block: "end" });
    }
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  // Auto-mark conversation as read whenever thread is open & in view
  useEffect(() => {
    if (isOpen && activeConversationId) {
      chatAPI
        .markAsRead(activeConversationId)
        .then((res) => {
          if (typeof res?.unreadCount === "number") {
            setUnreadCount(res.unreadCount);
          } else {
            fetchUnreadCount();
          }
          fetchConversations();
        })
        .catch(() => {});
    }
  }, [isOpen, activeConversationId, fetchUnreadCount, fetchConversations, setUnreadCount]);

  // Re-verify read status when user refocuses the browser window
  useEffect(() => {
    const handleFocus = () => {
      if (isOpen && activeConversationId) {
        chatAPI
          .markAsRead(activeConversationId)
          .then((res) => {
            if (typeof res?.unreadCount === "number") {
              setUnreadCount(res.unreadCount);
            } else {
              fetchUnreadCount();
            }
            fetchConversations();
          })
          .catch(() => {});
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [isOpen, activeConversationId, fetchUnreadCount, fetchConversations, setUnreadCount]);

  // Auto-scroll to bottom of messages like WhatsApp on thread open, message change, and finish loading
  useEffect(() => {
    if (!isOpen || !activeContact) return;

    // 1. Immediate scroll
    scrollToBottom("auto");

    // 2. Next animation frame
    const frameId = requestAnimationFrame(() => {
      scrollToBottom("auto");
    });

    // 3. Staggered timers to handle DOM paint, layout shifts, fonts, and async images
    const t1 = setTimeout(() => scrollToBottom("auto"), 50);
    const t2 = setTimeout(() => scrollToBottom("auto"), 150);
    const t3 = setTimeout(() => scrollToBottom("auto"), 300);
    const t4 = setTimeout(() => scrollToBottom("auto"), 600);

    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [messages, loadingMessages, isOpen, activeContact, isOtherTyping, scrollToBottom]);

  // Focus input when conversation opens
  useEffect(() => {
    if (activeContact && inputRef.current) {
      inputRef.current.focus();
    }
  }, [activeContact]);

  // Determine active project from URL pathname or currentProject
  const currentProjectId = useMemo(() => {
    const match = location?.pathname?.match(/\/project\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : (currentProject?._id || currentProject?.id || null);
  }, [location?.pathname, currentProject]);

  // Available projects list (combines backend projectContacts and ProjectContext projects)
  const availableProjects = useMemo(() => {
    const map = new Map();
    (projectContacts || []).forEach((p) => {
      const pid = p._id || p.id;
      if (pid) {
        map.set(pid, {
          _id: pid,
          id: pid,
          name: p.name || "Untitled Project",
          members: p.members || [],
          membersCount: p.membersCount ?? p.members?.length ?? 0,
        });
      }
    });
    (projects || []).forEach((p) => {
      const pid = p._id || p.id;
      if (pid && !map.has(pid)) {
        map.set(pid, {
          _id: pid,
          id: pid,
          name: p.name || "Untitled Project",
          members: [],
          membersCount: Array.isArray(p.members) ? p.members.length : 0,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );
  }, [projectContacts, projects]);

  // Resolved project ID: defaults strictly to the current active project
  const resolvedProjectId = useMemo(() => {
    if (currentProjectId) return currentProjectId;
    return availableProjects.length > 0 ? availableProjects[0]._id : "all";
  }, [currentProjectId, availableProjects]);

  // Members list for the selected single project
  const currentMembersList = useMemo(() => {
    if (resolvedProjectId === "all") return [];

    const memberMap = new Map();

    // From availableProjects
    const proj = availableProjects.find((p) => p._id === resolvedProjectId);
    if (proj?.members) {
      proj.members.forEach((m) => {
        const mid = m.id || m._id;
        if (mid) memberMap.set(mid, m);
      });
    }

    // From contacts matching this project
    (contacts || []).forEach((c) => {
      const cid = c.id || c._id;
      if (cid && c.projectIds?.includes(resolvedProjectId)) {
        if (!memberMap.has(cid)) {
          memberMap.set(cid, c);
        }
      }
    });

    // From currentProject if currently loaded
    if (
      currentProject &&
      (currentProject._id === resolvedProjectId || currentProject.id === resolvedProjectId) &&
      Array.isArray(currentProject.members)
    ) {
      currentProject.members.forEach((m) => {
        const u = m.user || (m.name ? m : null);
        if (u && (u._id || u.id)) {
          const uid = (u._id || u.id).toString();
          if (!memberMap.has(uid)) {
            memberMap.set(uid, {
              _id: uid,
              id: uid,
              name: u.name,
              email: u.email,
              avatar: u.avatar,
              color: u.color,
              role: u.role || m.role || "Member",
            });
          }
        }
      });
    }

    return Array.from(memberMap.values()).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );
  }, [resolvedProjectId, availableProjects, contacts, currentProject]);

  // Filtered members for single project with search
  const filteredSingleMembers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return currentMembersList;
    return currentMembersList.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.role?.toLowerCase().includes(q)
    );
  }, [currentMembersList, searchTerm]);

  // Grouped projects with members for "All Projects"
  const groupedProjects = useMemo(() => {
    if (resolvedProjectId !== "all") return [];
    const q = searchTerm.trim().toLowerCase();

    return availableProjects
      .map((p) => {
        const memberMap = new Map();
        (p.members || []).forEach((m) => {
          const mid = m.id || m._id;
          if (mid) memberMap.set(mid, m);
        });
        (contacts || []).forEach((c) => {
          const cid = c.id || c._id;
          if (cid && c.projectIds?.includes(p._id)) {
            if (!memberMap.has(cid)) memberMap.set(cid, c);
          }
        });
        let members = Array.from(memberMap.values()).sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );
        if (q) {
          members = members.filter(
            (c) =>
              c.name?.toLowerCase().includes(q) ||
              c.email?.toLowerCase().includes(q) ||
              c.role?.toLowerCase().includes(q)
          );
        }
        return {
          ...p,
          members,
        };
      })
      .filter((p) => p.members.length > 0);
  }, [resolvedProjectId, availableProjects, contacts, searchTerm]);

  // Filtered conversations for "Recent" tab
  const filteredConversations = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (conv) =>
        conv.with?.name?.toLowerCase().includes(q) ||
        conv.with?.email?.toLowerCase().includes(q) ||
        conv.lastMessage?.preview?.toLowerCase().includes(q)
    );
  }, [conversations, searchTerm]);

  // Group messages by day for date separators with strict deduplication
  const groupedMessages = useMemo(() => {
    const items = [];
    const seenIds = new Set();
    const uniqueMessages = messages.filter((m) => {
      const id = m._id || m.id;
      if (!id) return true;
      if (seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });

    uniqueMessages.forEach((m, idx) => {
      const prev = uniqueMessages[idx - 1];
      const prevDate = prev ? new Date(prev.createdAt).toDateString() : null;
      const currDate = new Date(m.createdAt).toDateString();

      if (prevDate !== currDate) {
        items.push({
          type: "day_divider",
          key: `day-${m.id || m._id}-${idx}`,
          label: fmtDayLabel(m.createdAt),
        });
      }

      items.push({
        type: "message",
        key: `msg-${m.id || m._id || idx}`,
        message: m,
      });
    });
    return items;
  }, [messages]);

  // Check if last sent message was seen by recipient
  const lastMineMessage = useMemo(() => {
    return [...messages].reverse().find((m) => m.mine);
  }, [messages]);

  const isLastMineSeen =
    lastMineMessage &&
    otherLastReadAt &&
    new Date(otherLastReadAt) >= new Date(lastMineMessage.createdAt);

  // Handle typing notification
  const handleInputChange = (e) => {
    setDraft(e.target.value);
    notifyTyping(true);

    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      notifyTyping(false);
    }, 2000);
  };

  // Handle file attachment selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploading(true);
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));

    try {
      const res = await chatAPI.uploadAttachments(formData);
      if (res.success && res.files) {
        setPendingUploads((prev) => [...prev, ...res.files]);
      }
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePendingUpload = (idx) => {
    setPendingUploads((prev) => prev.filter((_, i) => i !== idx));
  };

  const isSendingRef = useRef(false);

  // Submit message
  const handleSend = (e) => {
    e?.preventDefault();
    if (isSendingRef.current) return;

    const text = draft.trim();
    if (!text && !pendingUploads.length) return;

    isSendingRef.current = true;
    const uploadsToSend = [...pendingUploads];

    setDraft("");
    setPendingUploads([]);
    notifyTyping(false);
    setTimeout(() => scrollToBottom("smooth"), 10);

    Promise.resolve(
      sendMessage({
        body: text,
        attachments: uploadsToSend,
      })
    ).finally(() => {
      setTimeout(() => scrollToBottom("smooth"), 50);
      setTimeout(() => {
        isSendingRef.current = false;
      }, 300);
    });
  };

  const renderContactCard = (contact) => (
    <div
      key={contact.id || contact._id}
      onClick={() => openChatWithUser(contact)}
      className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-blue-50/50 dark:hover:bg-slate-800/80 cursor-pointer transition-colors group"
    >
      {/* Avatar Initials with Online Green Dot */}
      <div className="relative shrink-0">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${getAvatarBg(
            contact.name
          )}`}
        >
          {getInitials(contact.name)}
        </div>
        {isUserOnline?.(contact.id || contact._id) && (
          <span
            className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-900"
            title="Online"
          />
        )}
      </div>

      {/* Name & Email */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-1.5">
          <span className="text-xs font-semibold text-gray-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {contact.name}
          </span>
          {/* Role Badge */}
          <span
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${getRoleBadgeClass(
              contact.role
            )}`}
          >
            {contact.role || "Member"}
          </span>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-slate-400 truncate mt-0.5">
          {contact.email}
        </p>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed right-6 bottom-6 z-[9999] w-[370px] sm:w-[390px] h-[580px] max-w-[calc(100vw-24px)] max-h-[calc(100vh-24px)] bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-slideDownFade font-sans"
      style={{
        boxShadow: "0 20px 50px rgba(15, 23, 42, 0.22)",
      }}
    >
      {/* ─── VIEW 1: CONVERSATION THREAD ─── */}
      {activeContact ? (
        <>
          {/* Thread Header */}
          <div className="flex items-center justify-between px-3.5 py-3 border-b border-gray-150 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <button
                type="button"
                onClick={backToList}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                title="Back to conversations"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="relative shrink-0">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${getAvatarBg(
                    activeContact.name
                  )}`}
                >
                  {getInitials(activeContact.name)}
                </div>
                {isUserOnline?.(activeContact.id || activeContact._id) && (
                  <span
                    className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-900"
                    title="Online"
                  />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-bold text-gray-900 dark:text-white truncate leading-tight">
                  {activeContact.name}
                </h4>
                <p className="text-[11px] text-gray-500 dark:text-slate-400 truncate">
                  {activeContact.role || "Member"}
                  {activeContact.email ? ` · ${activeContact.email}` : ""}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={closeChat}
              className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              title="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages Scroll Area */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#fafbfe] dark:bg-slate-950/60"
          >
            {loadingMessages ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-xs gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span>Loading messages...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-4 py-8">
                <div
                  className={`w-14 h-14 rounded-full flex items-center justify-center text-base font-bold mb-3 ${getAvatarBg(
                    activeContact.name
                  )}`}
                >
                  {getInitials(activeContact.name)}
                </div>
                <p className="text-sm font-bold text-gray-800 dark:text-slate-200">
                  Start your conversation with {activeContact.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 max-w-[240px]">
                  Direct messages between team members are private and real-time.
                </p>
              </div>
            ) : (
              groupedMessages.map((item) => {
                if (item.type === "day_divider") {
                  return (
                    <div
                      key={item.key}
                      className="flex items-center justify-center my-3"
                    >
                      <span className="px-3 py-0.5 rounded-full text-[11px] font-medium bg-white dark:bg-slate-800 text-gray-500 dark:text-slate-400 border border-gray-200/80 dark:border-slate-700/80 shadow-xs">
                        {item.label}
                      </span>
                    </div>
                  );
                }

                const msg = item.message;
                const isMine = msg.mine;
                const isFailed = msg.failed;
                const isPending = msg.pending;

                return (
                  <div
                    key={item.key}
                    className={`flex flex-col ${
                      isMine ? "items-end" : "items-start"
                    }`}
                  >
                    {/* Text Bubble */}
                    {msg.body && (
                      <div
                        className={`max-w-[80%] px-3.5 py-2 text-[13px] leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere] ${
                          isMine
                            ? "bg-blue-600 text-white rounded-[16px] rounded-br-[6px] shadow-xs"
                            : "bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-[16px] rounded-bl-[6px] border border-gray-200/80 dark:border-slate-700/80 shadow-xs"
                        } ${isFailed ? "border border-red-500" : ""}`}
                      >
                        {msg.body}
                      </div>
                    )}

                    {/* Attachments */}
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="mt-1 space-y-1 max-w-[80%]">
                        {msg.attachments.map((att, attIdx) => {
                          const isImg =
                            att.fileType?.startsWith("image/") ||
                            /\.(jpg|jpeg|png|gif|webp)$/i.test(att.fileName);

                          return isImg ? (
                            <a
                              key={attIdx}
                              href={imageUrl(att.fileUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 max-h-48 group relative"
                            >
                              <img
                                src={imageUrl(att.fileUrl)}
                                alt={att.fileName}
                                onLoad={() => scrollToBottom("auto")}
                                className="w-full h-auto object-cover group-hover:opacity-95 transition-opacity"
                              />
                            </a>
                          ) : (
                            <a
                              key={attIdx}
                              href={imageUrl(att.fileUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 p-2 rounded-xl text-xs ${
                                isMine
                                  ? "bg-blue-700/90 text-white"
                                  : "bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-200 border border-gray-200 dark:border-slate-700"
                              }`}
                            >
                              <FileText className="w-4 h-4 shrink-0" />
                              <span className="truncate flex-1 font-medium">
                                {att.fileName}
                              </span>
                              <Download className="w-3.5 h-3.5 opacity-70" />
                            </a>
                          );
                        })}
                      </div>
                    )}

                    {/* Message Meta: Clock + Read status */}
                    <div className="flex items-center gap-1 mt-[3px] px-1 text-[10.5px] text-gray-400 dark:text-slate-400">
                      <span>{fmtClock(msg.createdAt)}</span>
                      {isMine && isLastMineSeen && msg.id === lastMineMessage?.id && (
                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                          · Seen
                        </span>
                      )}
                      {isPending && (
                        <span className="italic">
                          · Sending...
                        </span>
                      )}
                      {isFailed && (
                        <span className="text-red-500 inline-flex items-center gap-0.5 font-medium">
                          <AlertCircle className="w-2.5 h-2.5" /> Failed
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}

            {/* Typing Indicator */}
            {isOtherTyping && (
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400 pl-1 py-1">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-ping" />
                <span>{activeContact.name} is typing...</span>
              </div>
            )}

            {/* WhatsApp-style anchor element to guarantee bottom scroll */}
            <div ref={messagesEndRef} className="h-px w-full shrink-0" />
          </div>

          {/* Pending Uploads Chip Preview */}
          {pendingUploads.length > 0 && (
            <div className="px-3 py-2 bg-gray-50 dark:bg-slate-800/80 border-t border-gray-150 dark:border-slate-800 flex flex-wrap gap-1.5">
              {pendingUploads.map((u, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 px-2 py-1 bg-white dark:bg-slate-700 rounded-lg text-xs border border-gray-200 dark:border-slate-600 shadow-xs"
                >
                  <Paperclip className="w-3 h-3 text-gray-400" />
                  <span className="max-w-[120px] truncate font-medium text-gray-700 dark:text-slate-200">
                    {u.fileName}
                  </span>
                  <button
                    type="button"
                    onClick={() => removePendingUpload(i)}
                    className="text-gray-400 hover:text-red-500 p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Composer Input Bar */}
          <form
            onSubmit={handleSend}
            className="p-3 bg-white dark:bg-slate-900 border-t border-gray-150 dark:border-slate-800 flex items-center gap-2 shrink-0"
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />

            {/* Attachment Button */}
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="w-9 h-9 rounded-full flex items-center justify-center border border-gray-200 dark:border-slate-700 text-gray-500 hover:text-gray-800 dark:hover:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shrink-0 disabled:opacity-50"
              title="Attach files"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              ) : (
                <Paperclip className="w-4 h-4" />
              )}
            </button>

            {/* Message Input Box */}
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                placeholder={`Message ${activeContact.name || "..."}`}
                value={draft}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2 rounded-xl text-sm border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition-all"
              />
            </div>

            {/* Send Button */}
            <button
              type="submit"
              disabled={uploading || (!draft.trim() && !pendingUploads.length)}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white transition-colors shrink-0 disabled:opacity-40 disabled:hover:bg-blue-600 shadow-xs"
              title="Send message"
            >
              <Send className="w-4 h-4 translate-x-[-1px] translate-y-[-0.5px]" />
            </button>
          </form>
        </>
      ) : (
        /* ─── VIEW 2: LIST VIEW (RECENT & PEOPLE TABS) ─── */
        <>
          {/* Header */}
          <div className="px-5 pt-4 pb-3 flex items-start justify-between border-b border-gray-100 dark:border-slate-800 shrink-0">
            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white tracking-tight">
                Team chat
              </h3>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                Direct messages with your team members
              </p>
            </div>
            <button
              type="button"
              onClick={closeChat}
              className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              title="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Tab Switcher: Recent | People */}
          <div className="px-4 pt-3 pb-2 shrink-0">
            <div className="flex items-center p-1 bg-gray-100 dark:bg-slate-800 rounded-xl border border-gray-200/50 dark:border-slate-700/50">
              <button
                type="button"
                onClick={() => setActiveTab("recent")}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all duration-150 ${
                  activeTab === "recent"
                    ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-xs"
                    : "text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                Recent
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("people")}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all duration-150 ${
                  activeTab === "people"
                    ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-xs"
                    : "text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                People
              </button>
            </div>
          </div>

          {/* Search Box */}
          <div className="px-4 py-2 shrink-0">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search people..."
                className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition-all"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Tab Content List */}
          <div className="flex-1 overflow-y-auto px-2 py-1 space-y-1">
            {activeTab === "people" ? (
              /* People Directory List */
              resolvedProjectId === "all" ? (
                /* Grouped by Project */
                groupedProjects.length === 0 ? (
                  <div className="py-12 text-center text-xs text-gray-500 dark:text-slate-400">
                    {searchTerm
                      ? "No team members match your search"
                      : "No team members found"}
                  </div>
                ) : (
                  groupedProjects.map((group) => (
                    <div key={group._id} className="mb-2.5">
                      <div className="flex items-center justify-between px-2.5 py-1 text-[11px] font-bold text-gray-700 dark:text-slate-300 bg-gray-100/80 dark:bg-slate-800/80 rounded-lg mb-1 border border-gray-200/50 dark:border-slate-700/50">
                        <span className="truncate flex items-center gap-1.5">
                          📁 {group.name}
                        </span>
                        <span className="text-[10px] font-medium text-gray-400 dark:text-slate-400 shrink-0">
                          {group.members.length} {group.members.length === 1 ? "member" : "members"}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {group.members.map((contact) => renderContactCard(contact))}
                      </div>
                    </div>
                  ))
                )
              ) : (
                /* Single Project Members */
                filteredSingleMembers.length === 0 ? (
                  <div className="py-12 text-center text-xs text-gray-500 dark:text-slate-400">
                    {searchTerm
                      ? "No members match your search"
                      : "No other members in this project"}
                  </div>
                ) : (
                  filteredSingleMembers.map((contact) => renderContactCard(contact))
                )
              )
            ) : (
              /* Recent Conversations List */
              filteredConversations.length === 0 ? (
                <div className="py-12 text-center text-xs text-gray-500 dark:text-slate-400">
                  {searchTerm
                    ? "No conversations match your search"
                    : "No recent conversations yet. Switch to People to start one!"}
                </div>
              ) : (
                filteredConversations.map((conv) => (
                  <div
                    key={conv.id || conv._id}
                    onClick={() => openConversation(conv)}
                    className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-blue-50/50 dark:hover:bg-slate-800/80 cursor-pointer transition-colors group"
                  >
                    {/* Avatar with Online Green Dot */}
                    <div className="relative shrink-0">
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${getAvatarBg(
                          conv.with?.name
                        )}`}
                      >
                        {getInitials(conv.with?.name)}
                      </div>
                      {isUserOnline?.(conv.with?.id || conv.with?._id) && (
                        <span
                          className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-900"
                          title="Online"
                        />
                      )}
                    </div>

                    {/* Name, Preview, Time & Unread */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <span className="text-xs font-semibold text-gray-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {conv.with?.name}
                        </span>
                        <span className="text-[10px] text-gray-400 dark:text-slate-500 shrink-0">
                          {fmtListTime(conv.lastMessageAt)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-1">
                        <p className="text-[11px] text-gray-500 dark:text-slate-400 truncate flex-1">
                          {conv.lastMessage ? (
                            conv.lastMessage.mine ? (
                              <span>You: {conv.lastMessage.preview}</span>
                            ) : (
                              <span>{conv.lastMessage.preview}</span>
                            )
                          ) : (
                            <span className="italic">Started conversation</span>
                          )}
                        </p>

                        {conv.unread > 0 && (
                          <span className="w-2 h-2 rounded-full bg-blue-600 shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )
            )}
          </div>
        </>
      )}
    </div>,
    document.body
  );
};
